import {
  Tenant,
  staticError,
  useAuthFetch,
  useGuardedEffect,
  useHasRole,
} from "@p0security/frontend-core";
import { ErrorDisplay, useFirestoreDoc } from "@p0security/frontend-core/react";
import type {
  JiraTrackerIntegration,
  JiraTrackerProject,
} from "@p0security/integrations-jira-tracker-shared/types";
import { Button, Flex, Input, Select, Spin, Typography } from "@p0security/ui";
import { useCallback, useContext, useMemo, useState } from "react";

const { Paragraph, Text } = Typography;

/** The tracker's settings routes, beside its install routes. */
const PROJECTS_PATH = "integrations/jira-tracker/projects";
const PROJECT_PATH = "integrations/jira-tracker/config/project";

/** Whether the tracker has a site whose install check found its cloud ID,
 * which is when P0 can list projects. */
const hasInstalledSite = (config: JiraTrackerIntegration) =>
  Object.values(config.site ?? {}).some(
    (site) => site.state === "installed" && !!site.cloudId
  );

const ProjectForm: React.FC<{ config: JiraTrackerIntegration }> = ({
  config,
}) => {
  const [error, setError] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);
  const authFetch = useAuthFetch(setError, setIsFetching);
  const [loadError, setLoadError] = useState<string>();
  const loadFetch = useAuthFetch(setLoadError);
  const [loads, setLoads] = useState(0);
  const [saved, setSaved] = useState(false);
  const canEdit = useHasRole("owner");
  const [projects, setProjects] = useState<JiraTrackerProject[]>();
  const [projectId, setProjectId] = useState(config.project?.id);
  const [parentIssueType, setParentIssueType] = useState(
    config.parentIssueType ?? ""
  );

  useGuardedEffect(
    (cancellation) => async () => {
      if (!canEdit) return;
      const response = await loadFetch(PROJECTS_PATH, { method: "GET" });
      if (!response) return;
      const { projects } = await response.json();
      cancellation.guard(setProjects)(projects);
    },
    [loadFetch, canEdit, loads],
    staticError
  );

  const retryLoad = useCallback(() => {
    setLoadError(undefined);
    setLoads((count) => count + 1);
  }, []);

  const options = useMemo(
    () =>
      projects?.map((p) => ({ value: p.id, label: `${p.name} (${p.key})` })),
    [projects]
  );

  const save = useCallback(async () => {
    if (!projectId) return;
    setSaved(false);
    const response = await authFetch(PROJECT_PATH, {
      method: "PUT",
      json: { id: projectId, parentIssueType },
    });
    if (response) setSaved(true);
  }, [authFetch, parentIssueType, projectId]);

  const onSave = useCallback(() => void save(), [save]);

  const clearError = useCallback(() => setError(undefined), []);

  const onParentIssueTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      setParentIssueType(event.target.value),
    []
  );

  if (!canEdit) {
    return config.project ? (
      <Paragraph>
        P0 files findings in {config.project.name} ({config.project.key}).
      </Paragraph>
    ) : null;
  }

  return (
    <Flex gap="0.7em" vertical>
      {error && (
        <ErrorDisplay
          title="Error saving the Jira project"
          error={error}
          onClose={clearError}
        />
      )}
      {!config.project && (
        <Paragraph strong>
          This tracker files no findings until you save a project.
        </Paragraph>
      )}
      {loadError ? (
        <Flex gap="0.7em" vertical>
          <ErrorDisplay
            title="Error listing the Jira projects"
            error={loadError}
            onClose={retryLoad}
          />
          <div>
            <Button onClick={retryLoad}>Retry</Button>
          </div>
        </Flex>
      ) : !projects ? (
        <Spin />
      ) : !projects.length ? (
        <Paragraph>
          The service account can&apos;t browse any project. Give it the
          permissions below on the project P0 files findings in.
        </Paragraph>
      ) : (
        <>
          <Text>Select the project where P0 will create issues:</Text>
          <Select
            aria-label="Jira project"
            onChange={setProjectId}
            options={options}
            placeholder="Select a project"
            /* eslint-disable-line react/forbid-component-props */ style={{
              maxWidth: "30em",
            }}
            value={projectId}
          />
          <Text>
            The issue type of the parent issue P0 files each finding&apos;s
            issue under, if not Epic. It has to exist in the project, as Task,
            the type of each finding&apos;s issue, does:
          </Text>
          <Input
            aria-label="Parent issue type"
            onChange={onParentIssueTypeChange}
            placeholder="Epic"
            /* eslint-disable-line react/forbid-component-props */ style={{
              maxWidth: "30em",
            }}
            value={parentIssueType}
          />
          <div>
            <Button
              disabled={!projectId}
              loading={isFetching}
              onClick={onSave}
              type="primary"
            >
              Save
            </Button>
          </div>
          {saved && config.project && (
            <Text type="success">
              Saved. P0 files findings in {config.project.name} (
              {config.project.key}).
            </Text>
          )}
        </>
      )}
    </Flex>
  );
};

/** The permissions the service account needs, as Jira names them. */
const PermissionsNote: React.FC = () => (
  <Paragraph type="secondary">
    The service account needs Browse projects, Create issues, Edit issues and
    Assign issues on the project, and the global permission Browse users and
    groups, to find each issue&apos;s assignee by email.
  </Paragraph>
);

/**
 * The tracker's settings, above its install components: once its site is
 * installed, the project P0 files findings in and the optional parent issue
 * type, as today's Jira tracker page has them. Saving checks the service
 * account's permissions on the project, and the tracker files nothing until
 * a project is saved.
 */
export const TrackerSettings: React.FC = () => {
  const tenantId = useContext(Tenant);
  const { doc, loading } = useFirestoreDoc<JiraTrackerIntegration>(
    `o/${tenantId}/integrations/jira-tracker`,
    { live: true }
  );
  if (loading) return <Spin />;
  const config = doc?.data;
  return (
    <Flex gap="0.7em" vertical>
      <Paragraph>
        P0 files findings as Jira issues signed in as an Atlassian service
        account, with no app from the Atlassian Marketplace. You can set it up
        while Jira (Legacy) files your findings. Once you save a project here,
        P0 files findings here instead, and you can remove Jira (Legacy).
      </Paragraph>
      {config && hasInstalledSite(config) ? (
        <ProjectForm config={config} />
      ) : (
        <Paragraph>
          Add the Jira site below. Once it&apos;s installed, choose the project
          P0 files findings in here.
        </Paragraph>
      )}
      <PermissionsNote />
    </Flex>
  );
};
